import {ChecklistItemId} from '@udemy/plan-user-onboarding';

import {
    UserChecklistName,
    UserChecklistTaskName,
    UserChecklistTaskStatus,
    UserSettingName,
    type OnboardingChecklistQuery,
} from 'src/generated/graphql';

import {
    CHECKLIST_SUBSCRIPTION_VISIBILITY_WINDOW_MS,
    PP_CHECKLIST_WATCH_LESSONS_TOTAL,
} from './constants';

import type {OnboardingChecklistEnrollment, OnboardingNextCurriculumItem} from './types';

/**
 * Subscription start from the combined onboarding checklist query (`currentSubscriptionEnrollment`).
 */
export function getChecklistSubscriptionStartDateIso(
    data?: OnboardingChecklistQuery | null,
): string | null {
    const raw = data?.currentSubscriptionEnrollment?.startDate;
    if (typeof raw !== 'string') return null;
    return raw.trim() || null;
}

export function isWithinChecklistSubscriptionWindow(
    subscriptionStartDateIso: string | null | undefined,
    nowMs: number = Date.now(),
): boolean {
    const trimmed = subscriptionStartDateIso?.trim();
    if (!trimmed) {
        return true;
    }
    const startMs = Date.parse(trimmed);
    if (Number.isNaN(startMs)) {
        return true;
    }
    return nowMs - startMs < CHECKLIST_SUBSCRIPTION_VISIBILITY_WINDOW_MS;
}

/**
 * Returns the ONBOARDING checklist row from the combined checklist query when present
 * (including when `tasks` is still empty — task list is filled by the API over time).
 */
export function getOnboardingUserChecklist(
    data?: OnboardingChecklistQuery | null,
): OnboardingChecklistQuery['userChecklists'][number] | null {
    const rows = data?.userChecklists ?? [];
    for (const row of rows) {
        if (row?.name === UserChecklistName.Onboarding) {
            return row;
        }
    }
    return null;
}

export function hasOnboardingUserChecklistRow(data?: OnboardingChecklistQuery | null): boolean {
    return getOnboardingUserChecklist(data) != null;
}

function isTaskCompleted(
    checklist: NonNullable<ReturnType<typeof getOnboardingUserChecklist>>,
    taskName: UserChecklistTaskName,
): boolean {
    for (const task of checklist.tasks ?? []) {
        if (task.name === taskName) {
            return task.status === UserChecklistTaskStatus.Completed;
        }
    }
    return false;
}

/**
 * Returns the `initialStep` to pass to `OnboardingModal` so the user resumes where they left off.
 *
 * `lastViewedPageNumber` is stored as a 0-based index of the last page the user continued *from*
 * (`currentStep - 1`). `OnboardingModal.initialStep` is 1-based, so the resume step is
 * `lastViewedPageNumber + 2`. Falls back to `1` (first step) when no tracking exists.
 */
export function getOnboardingModalInitialStep(
    data: OnboardingChecklistQuery | null | undefined,
): number {
    const checklist = getOnboardingUserChecklist(data ?? null);
    if (!checklist) return 1;
    for (const task of checklist.tasks ?? []) {
        if (task.name === UserChecklistTaskName.SeeOnboardingModals) {
            const lastViewedPage = task.pageTracking?.lastViewedPageNumber;
            if (lastViewedPage == null) return 1;
            const step = Number(lastViewedPage) + 2;
            return Number.isNaN(step) ? 1 : step;
        }
    }
    return 1;
}

/** Whether the given task is already completed in the latest checklist query data (avoids duplicate `userChecklistTaskComplete` calls). */
export function isOnboardingChecklistTaskCompleted(
    data: OnboardingChecklistQuery | null | undefined,
    taskName: UserChecklistTaskName,
): boolean {
    const checklist = getOnboardingUserChecklist(data ?? null);
    if (!checklist) {
        return false;
    }
    return isTaskCompleted(checklist, taskName);
}

/**
 * Derives completed checklist item ids from `userChecklists` task status.
 */
export function getChecklistCompletedItemIdsFromUserChecklist(
    data: OnboardingChecklistQuery,
    completedLessons: string[],
    watchLessonsTotal: number,
): ChecklistItemId[] {
    const checklist = getOnboardingUserChecklist(data);
    if (!checklist) {
        return [];
    }

    const completedItemIds: ChecklistItemId[] = [];

    if (isTaskCompleted(checklist, UserChecklistTaskName.SeeOnboardingModals)) {
        completedItemIds.push(ChecklistItemId.LearnPlan);
    }
    if (isTaskCompleted(checklist, UserChecklistTaskName.UpdateSkillsAndOccupationSurvey)) {
        completedItemIds.push(ChecklistItemId.CustomizeInterests);
    }
    if (isTaskCompleted(checklist, UserChecklistTaskName.DownloadApp)) {
        completedItemIds.push(ChecklistItemId.GetApp);
    }
    if (isTaskCompleted(checklist, UserChecklistTaskName.CompleteFiveItems)) {
        completedItemIds.push(ChecklistItemId.WatchLessons);
    } else if (completedLessons.length >= watchLessonsTotal) {
        // Enrollment data can lead the checklist task until the next checklist refetch.
        completedItemIds.push(ChecklistItemId.WatchLessons);
    }
    if (isTaskCompleted(checklist, UserChecklistTaskName.CheckOutAssessments)) {
        completedItemIds.push(ChecklistItemId.Assessments);
    }

    return completedItemIds;
}

/**
 * Watch-lessons count for the checklist UI: prefers API progress when present.
 */
export function getWatchLessonsCountForChecklist(
    data: OnboardingChecklistQuery | null | undefined,
    completedLessonsLength: number,
): number {
    const checklist = getOnboardingUserChecklist(data ?? null);
    if (!checklist) return completedLessonsLength;
    for (const task of checklist.tasks ?? []) {
        if (task.name === UserChecklistTaskName.CompleteFiveItems && task.progress) {
            return Math.max(task.progress.completedCount, completedLessonsLength);
        }
    }
    return completedLessonsLength;
}

export function getChecklistCompletedLessons(data?: OnboardingChecklistQuery | null): string[] {
    const enrollments = data?.me?.enrollments?.courseEnrollmentsPaged?.courseEnrollments ?? [];

    const ids: string[] = [];
    for (const enrollment of enrollments) {
        if (!enrollment) continue;
        for (const item of enrollment.completedItems ?? []) {
            if (item?.__typename === 'VideoLecture' && item.id) {
                ids.push(item.id);
                if (ids.length >= PP_CHECKLIST_WATCH_LESSONS_TOTAL) {
                    return ids;
                }
            }
        }
    }
    return ids;
}

/** First non-null enrollment; the checklist query orders by lastAccessedTime DESC. */
function getLatestAccessedEnrollment(
    enrollments: Array<OnboardingChecklistEnrollment | null>,
): OnboardingChecklistEnrollment | null {
    return enrollments.find((e): e is OnboardingChecklistEnrollment => e != null) ?? null;
}

function getNextCurriculumItemUrlLanding(
    nextItem?: OnboardingNextCurriculumItem | null,
): string | null {
    if (!nextItem || !('urlLanding' in nextItem) || !nextItem.urlLanding) {
        return null;
    }
    return String(nextItem.urlLanding);
}

export function getWatchLessonsUrl(data?: OnboardingChecklistQuery | null): string | null {
    const enrollments = data?.me?.enrollments?.courseEnrollmentsPaged?.courseEnrollments ?? [];
    const latestEnrollment = getLatestAccessedEnrollment(enrollments);
    if (!latestEnrollment) {
        return null;
    }
    return getNextCurriculumItemUrlLanding(latestEnrollment.course?.nextCurriculumItem);
}

/**
 * Returns true when the given `UserSetting` (from `me.userSettings`) has a boolean true value.
 * Handles the aliased `boolValue` field from the query fragment and absent / non-boolean values gracefully.
 */
export function getUserSettingBoolValue(
    data: OnboardingChecklistQuery | null | undefined,
    settingName: UserSettingName,
): boolean {
    const settings = data?.me?.userSettings ?? [];
    const setting = settings.find((s) => s?.name === settingName);
    if (setting?.value.__typename === 'UserSettingBooleanValue') {
        return setting.value.boolValue === true;
    }
    return false;
}

/**
 * Derives completed checklist item ids from `me.userSettings` (the Django UserSetting bridge).
 * Used as a fallback when the `userChecklists` ONBOARDING row is not yet present for the user —
 * e.g. users who completed steps before the new `userChecklists` service existed.
 * Watch-lessons completion cannot be derived from UserSettings, so it falls back to enrollment data.
 */
export function getChecklistCompletedItemIdsFromUserSettings(
    data: OnboardingChecklistQuery,
    completedLessons: string[],
    watchLessonsTotal: number,
): ChecklistItemId[] {
    const completedItemIds: ChecklistItemId[] = [];

    if (getUserSettingBoolValue(data, UserSettingName.OnboardingChecklistSeenOnboardingModals)) {
        completedItemIds.push(ChecklistItemId.LearnPlan);
    }
    if (
        getUserSettingBoolValue(
            data,
            UserSettingName.OnboardingChecklistCompletedSkillsAndOccupationSurvey,
        )
    ) {
        completedItemIds.push(ChecklistItemId.CustomizeInterests);
    }
    if (getUserSettingBoolValue(data, UserSettingName.OnboardingChecklistDownloadedMobileApp)) {
        completedItemIds.push(ChecklistItemId.GetApp);
    }
    if (completedLessons.length >= watchLessonsTotal) {
        completedItemIds.push(ChecklistItemId.WatchLessons);
    }

    return completedItemIds;
}

export interface GetChecklistCompletedItemIdsParams {
    completedLessons: string[];
    watchLessonsTotal: number;
    onboardingChecklistQuery?: OnboardingChecklistQuery | null;
}

/**
 * Completed checklist rows for the modal:
 * 1. Prefer `userChecklists` task status when the ONBOARDING row exists (new service).
 * 2. Fall back to `me.userSettings` (Django UserSetting bridge) for users who completed
 *    steps before the `userChecklists` service tracked them.
 * 3. Last resort: only the watch-lessons row can be inferred from enrollment data alone.
 */
export function getChecklistCompletedItemIds({
    completedLessons,
    watchLessonsTotal,
    onboardingChecklistQuery,
}: GetChecklistCompletedItemIdsParams): ChecklistItemId[] {
    if (onboardingChecklistQuery && hasOnboardingUserChecklistRow(onboardingChecklistQuery)) {
        return getChecklistCompletedItemIdsFromUserChecklist(
            onboardingChecklistQuery,
            completedLessons,
            watchLessonsTotal,
        );
    }

    if (onboardingChecklistQuery) {
        return getChecklistCompletedItemIdsFromUserSettings(
            onboardingChecklistQuery,
            completedLessons,
            watchLessonsTotal,
        );
    }

    if (completedLessons.length >= watchLessonsTotal) {
        return [ChecklistItemId.WatchLessons];
    }
    return [];
}
